import * as React from 'react';

import { TextBlockProps } from "./text-block.types";
import { SkipLogicCondition, SkipLogicConditionOperator } from '@common/components/skip-logic/skip-logic.types';
import { QuestionnaireSkipLogicConditionCreateDto } from '@common/dto/questionnaire/skip-logic/condition-create-dto';
import { generateId } from '@common/utils/common';
import { QuestionnaireSkipLogicConditionListDto } from '@common/dto/questionnaire/skip-logic/condition-list-dto';
import { QuestionnaireTextBlockListDto } from '@common/dto/questionnaire/text-block/list-dto';

export const useTextBlockController = (props: TextBlockProps) => {
  const { data, onChange } = props;

  const [fieldSkipLogicConditions, setSkipLogicConditions] = React.useState<Array<SkipLogicCondition>>([]);

  React.useEffect(() => {
    setSkipLogicConditions(data.skipLogic?.map((x) => ({ ...x, id: generateId() } as SkipLogicCondition)) || []);
  }, []);

  const updateTextBlock = React.useCallback((field: Partial<QuestionnaireTextBlockListDto>) => {
    onChange({
      ...data,
      ...field
    });
  }, [data, onChange]);

  const handleTextBlockNameChange = React.useCallback((val: string) => {
    updateTextBlock({ title: val.trim() });
  }, [updateTextBlock]);

  const handleTextBlockDescriptionChange = React.useCallback((val: string) => {
    updateTextBlock({ text: val });
  }, [updateTextBlock]);

  const handleUseSkipLogicChange = React.useCallback((val: string) => {
    let skipLogicEnabled = false;

    try {
      skipLogicEnabled = JSON.parse(val);
    } catch {}

    updateTextBlock({ skipLogicEnabled });
  }, [updateTextBlock]);

  const handleSkipLogicConditionAdd = React.useCallback(({ conditions } : { conditions: QuestionnaireSkipLogicConditionCreateDto[] }) => {
    const skipLogic: Array<SkipLogicCondition> = [
      ...fieldSkipLogicConditions,
      ...conditions.map((condition) => ({ ...condition, id: generateId(), operator: SkipLogicConditionOperator.equals } as SkipLogicCondition))
    ];

    updateTextBlock({
      skipLogic: skipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto)
    });
    setSkipLogicConditions(skipLogic);
  }, [updateTextBlock, fieldSkipLogicConditions]);

  const handleSkipLogicChange = React.useCallback((skipLogic: SkipLogicCondition[]) => {
    updateTextBlock({
      skipLogic: skipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto)
    });
    setSkipLogicConditions(skipLogic);
  }, [updateTextBlock]);

  return {
    fieldSkipLogicConditions,
    handleSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleUseSkipLogicChange,
    handleTextBlockNameChange,
    handleTextBlockDescriptionChange,
  };
};